import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import WebViewer from '@pdftron/webviewer';
import { X, Send } from 'lucide-react';

const EditCanvas = ({ isOpen, file, onCancel, onSave }) => {
    const viewerRef = useRef(null);
    const instanceRef = useRef(null);
    const [documentReady, setDocumentReady] = useState(false);

    // Get document source and name
    const docSource = (file && typeof file === 'object' && !(file instanceof File)) ? file.url : file;
    const docName = (file && typeof file === 'object') ? (file.name || file.title || file.filename) : 'document.docx';

    useEffect(() => {
        if (!isOpen || !docSource || !viewerRef.current) return;

        setDocumentReady(false);

        // Initialize WebViewer
        WebViewer(
            {
                path: '/lib',
                initialDoc: docSource,
                enableOfficeEditing: true, // Enable Microsoft Word editing
            },
            viewerRef.current
        ).then((instance) => {
            instanceRef.current = instance;

            const { documentViewer, LayoutMode, Feature } = instance.Core;

            // Set layout mode to single page to fit nicely
            instance.UI.setLayoutMode(LayoutMode.Single);

            // Customize UI: Hide unnecessary elements for better "Canvas" feel
            instance.UI.disableFeatures([Feature.NotesPanel, Feature.Print]);

            // Wait for document to be fully loaded
            documentViewer.addEventListener('documentLoaded', () => {
                console.log('Document fully loaded and ready');
                setDocumentReady(true);
            });

            console.log('WebViewer instance ready');
        });

        return () => {
            if (instanceRef.current) {
                // Cleanup 
                setDocumentReady(false);
            }
        };
    }, [isOpen, docSource]);

    const handleAnalyze = async () => {
        if (!instanceRef.current) {
            alert('Editor not initialized. Please try again.');
            return;
        }

        if (!documentReady) {
            alert('Please wait for the document to load completely before exporting.');
            return;
        }

        try {
            console.log('Starting Office document export...');

            const { UI } = instanceRef.current;

            // For Office editing, we need to use the Office Editor API
            // Get the office editor instance
            const officeEditor = UI.getOfficeEditor();

            if (!officeEditor) {
                throw new Error('Office editor not available. Please ensure the document is loaded.');
            }

            console.log('Office editor found, saving document...');

            // Save the document and get the blob
            // This returns a promise that resolves with the edited document as a blob
            const blob = await officeEditor.save();

            console.log('Document saved, blob size:', blob.size);

            if (!blob || blob.size === 0) {
                throw new Error('Exported document is empty');
            }

            // Convert blob to File
            const editedFile = new File([blob], docName, {
                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            });

            console.log('Export successful, file size:', editedFile.size);
            onSave(editedFile);
        } catch (error) {
            console.error('Export failed:', error);
            alert('Failed to export document: ' + error.message);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-md flex flex-col p-4 md:p-8 animate-fade-in">
            <div className="flex items-center justify-between mb-4 bg-white/10 p-4 rounded-2xl border border-white/20">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center border border-purple-500/40">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#a855f7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M2 17L12 22L22 17" stroke="#a855f7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M2 12L12 17L22 12" stroke="#a855f7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <div className="hidden sm:block">
                        <h3 className="text-white font-semibold text-lg leading-tight">Edit Canvas</h3>
                        <p className="text-white/50 text-xs">Review and modify your document before AI analysis</p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={onCancel}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-white/70 transition-all border border-white/10 text-sm md:text-base"
                    >
                        <X size={18} />
                        <span className="hidden xs:inline">Cancel</span>
                    </button>
                    <button
                        onClick={handleAnalyze}
                        disabled={!documentReady}
                        className={`flex items-center gap-2 px-6 py-2 rounded-xl font-medium transition-all shadow-lg text-sm md:text-base ${documentReady
                            ? 'bg-purple-600 hover:bg-purple-500 text-white shadow-purple-600/20'
                            : 'bg-gray-600 text-gray-400 cursor-not-allowed shadow-gray-600/20'
                            }`}
                    >
                        <Send size={18} />
                        <span>{documentReady ? 'Analyze with AI' : 'Loading document...'}</span>
                    </button>
                </div>
            </div>

            <div
                className="flex-1 rounded-2xl overflow-hidden bg-white border border-white/10 shadow-2xl relative"
                ref={viewerRef}
            ></div>
        </div>,
        document.body
    );
};

export default EditCanvas;

